import { graphql } from "gatsby";
import { EntityQuery, HeaderNode, InterfacesQuery, MemberFunction, MembersQuery } from "queries";
import React from "react";
import { resolveComplexTypes, resolveNestedProperties } from "../helper";
import type { Member } from "../types";
import DynamicPage from "./DynamicPage";

export const queryFragments = graphql`
    fragment Property on DocsJson {
        properties {
            name
            type {
                names
            }
            description
            optional
            defaultvalue
        }
    }
    fragment Returns on DocsJson {
        returns {
            description
            type {
                names
            }
        }
    }
    fragment Params on DocsJson {
        params {
            name
            optional
            description
            type {
                names
            }
        }
    }
    fragment Type on DocsJson {
        type {
            names
        }
    }
    fragment Member on DocsJson {
        ...Identity
        longname
        description
        defaultvalue
        ...Type
        ...Property
        ...Params
        ...Returns
    }
    fragment Identity on DocsJson {
        name
        docName
    }
`;
export const query = graphql`
    query ($name: String) {
        entityQuery: allDocsJson(filter: { name: { eq: $name }, undocumented: { ne: true } }) {
            nodes {
                ...Identity
                ...Property
                description
                classdesc
                examples
            }
        }
        membersQuery: allDocsJson(
            filter: {
                memberof: { eq: $name }
                undocumented: { ne: true }
                access: { ne: "private" }
                ignore: { ne: true }
            }
        ) {
            nodes {
                ...Member
                scope
                readonly
                kind
                examples
            }
        }
        interfacesQuery: allDocsJson(
            filter: {
                kind: { eq: "interface" }
                name: { regex: "/^(?!I|Theme$)/" }
                undocumented: { ne: true }
                access: { ne: "private" }
                ignore: { ne: true }
            }
        ) {
            nodes {
                ...Member
            }
        }
    }
`;
// To remove the duplicates from the derived nodes.
const filterDuplicateNodes = (nodes: [HeaderNode, ...Member[]]): any => {
    const filteredNodes: any = {};
    nodes.forEach((node) => {
        const currentNode: any = { ...node };
        // for methods
        if (currentNode.methods?.length) {
            const filteredMethods: any = {};
            currentNode.methods.forEach((method: any) => {
                if (filteredMethods[method.longname] === undefined) {
                    filteredMethods[method.longname] = method;
                }
            });
            currentNode.methods = Object.values(filteredMethods);
        }
        // for members
        // checking only longname as only that is used to generate the hash links for the sections
        if (filteredNodes[currentNode.longname] === undefined) {
            filteredNodes[currentNode.longname] = currentNode;
        }
    });
    return Object.values(filteredNodes);
};

export default function APITemplate(props: {
    data: {
        entityQuery: EntityQuery;
        membersQuery: MembersQuery;
        interfacesQuery: InterfacesQuery;
    };
}) {
    const data: {
        entityQuery: EntityQuery;
        membersQuery: MembersQuery;
        interfacesQuery: InterfacesQuery;
    } = JSON.parse(JSON.stringify(props.data)); // Avoids modifying query results
    const { name, docName, description, properties, examples, classdesc } = data.entityQuery.nodes[0];
    const { nodes: entityMembers } = data.membersQuery;
    const { nodes: interfaces } = data.interfacesQuery;

    // kind: constant, class, interface, member, typedef, function
    const typeDefs = [...interfaces, ...entityMembers.filter(({ kind }) => kind === "typedef")]
        .map((typeDef) => ({
            ...typeDef,
            properties: typeDef.properties?.reduce(resolveNestedProperties, [])
        }))
        .reduce((map: { [name: string]: Member }, typeDef) => {
            map[typeDef.longname] = typeDef;
            map[typeDef.name] = typeDef;
            return map;
        }, {});

    const members = entityMembers.filter(({ kind }) => kind === "member");

    const methods: MemberFunction[] = entityMembers
        .filter(({ kind }) => ["function"].includes(kind!))
        .map((method) => ({
            ...method,
            params: resolveComplexTypes(typeDefs, method.params),
            returns: resolveComplexTypes(typeDefs, method.returns)
        }));

    const nodes: [HeaderNode, ...Member[]] = [
        {
            examples,
            properties: resolveComplexTypes(typeDefs, properties),
            methods
        },
        ...members
    ];

    nodes.forEach((node) => {
        node.properties &&= node.properties?.reduce(resolveNestedProperties, []);
    });

    return (
        <DynamicPage
            name={docName || name}
            description={description || classdesc}
            nodes={filterDuplicateNodes(nodes)}
        />
    );
}
